import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faX } from '@fortawesome/free-solid-svg-icons';
import { Typography, TypographyProps } from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CustomDialogProps extends Omit<DialogProps, 'open'> {
  primaryColor?: boolean;
  neutralColor?: boolean;
  title?: string;
  icon?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  buttons?: boolean;
  modalState?: boolean;
  customMaxWidth?: string;
  onClose?: () => void;
  deleteConfirmationModal?: boolean;
  confirmationText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  withExtraField?: boolean;
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) =>
    !['primaryColor', 'neutralColor', 'title', 'icon', 'customMaxWidth', 'deleteConfirmationModal'].includes(
      prop as string
    ),
})<CustomDialogProps>(() => ({
  fontFamily: tsoPqTheme.typography.openSans,
  fontSize: tsoPqTheme.typography.fontsize_button_button_small,
  lineHeight: tsoPqTheme.typography.lineHeight_l,
  fontWeight: '400',
  fontStyle: 'normal',
  textTransform: 'none',
  '& .MuiDialog-paper': {
    maxWidth: 'none',
    minWidth: '25rem',
    display: 'flex',
    padding: '2rem',
    '& .MuiDialogContent-root ': {
      overflowX: 'hidden',
    },
  },
}));

const StyledTypography = styled(Typography)<TypographyProps>(() => {
  return {
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: '1.5rem',
    lineHeight: '2.125rem',
    fontWeight: '700',
    fontStyle: 'normal',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
  };
});

const StyledIconButton = styled(IconButtonTsoPq)(() => {
  return {
    position: 'absolute',
    right: 8,
    top: 8,
    fontSize: '0.875rem',
    height: '1.875rem',
    width: '1.875rem',
    minHeight: '1.875rem',
    minWidth: '1.875rem',
    border: 'none',
    '& svg': {
      fontSize: '0.875rem',
    },
  };
});

const StyledDialogTitle = styled(DialogTitle, {
  shouldForwardProp: (prop) => !['deleteconfirmationmodal', 'withextrafield'].includes(prop as string),
})<{
  deleteconfirmationmodal?: boolean;
  withextrafield?: boolean;
}>(({ deleteconfirmationmodal, withextrafield }) => {
  const condition = deleteconfirmationmodal || withextrafield;

  const marginBottom = condition
    ? tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24
    : tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16;

  return {
    padding: 0,
    marginBottom,
  };
});

const StyledDialogContent = styled(DialogContent)(() => {
  return {
    padding: 0,
  };
});

const ModalContentWrapper = styled('div', {
  shouldForwardProp: (prop) => !['deleteConfirmationModal', 'withExtraField'].includes(prop as string),
})<{
  maxWidth: string;
  deleteConfirmationModal?: boolean;
  withExtraField?: boolean;
}>(({ maxWidth, deleteConfirmationModal, withExtraField }) => {
  const gapMap = {
    withExtraField: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
    deleteConfirmationModal: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
    default: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  };

  type GapType = keyof typeof gapMap;

  let type: GapType = 'default';
  if (deleteConfirmationModal) type = 'deleteConfirmationModal';
  if (withExtraField) type = 'withExtraField';

  const gap = gapMap[type];

  return {
    maxWidth,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap,
  };
});

const StyledDialogActions = styled(DialogActions, {
  shouldForwardProp: (prop) => prop !== 'deleteConfirmationModal',
})<{ maxWidth?: string; deleteConfirmationModal?: boolean }>(({ maxWidth, deleteConfirmationModal }) => ({
  maxWidth: maxWidth ?? '100%',
  width: '100%',
  margin: '0 auto',
  padding: '1rem 0 0 0',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_16,
}));

const ModalDialogTsoPq: React.FC<CustomDialogProps> = ({
  primaryColor,
  neutralColor,
  title,
  icon = false,
  children,
  actions,
  onClose,
  buttons = false,
  modalState = false,
  customMaxWidth,
  deleteConfirmationModal = false,
  confirmationText,
  onConfirm,
  onCancel,
  withExtraField = false,
  ...props
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    setOpen(modalState);
  }, [modalState]);

  // --- Dialog with custom max width, changen't ---
  if (customMaxWidth) {
    return (
      <StyledDialog aria-labelledby="customized-dialog-title" {...props} open={open}>
        <StyledDialogTitle id="customized-dialog-title" deleteconfirmationmodal={deleteConfirmationModal}>
          {icon && (
            <StyledIconButton
              disableRipple
              aria-label="close"
              onClick={onClose}
              sizeVariant="s"
              icon={faX}
              buttonType="transparentNeutral"
            />
          )}
          <StyledTypography>{title}</StyledTypography>
        </StyledDialogTitle>

        <StyledDialogContent>
          <ModalContentWrapper maxWidth={customMaxWidth} deleteConfirmationModal={deleteConfirmationModal}>
            {deleteConfirmationModal ? (
              <>
                <TypographyTsoPq typographyType="bodyTextMD">{confirmationText}</TypographyTsoPq>
                <StyledDialogActions maxWidth={customMaxWidth} deleteConfirmationModal={deleteConfirmationModal}>
                  <ButtonTsoPq buttonType="outlinedPrimary" onClick={onCancel} textContent="Cancel" />
                  <ButtonTsoPq buttonType="filledPrimary" onClick={onConfirm} textContent="Delete" />
                </StyledDialogActions>
              </>
            ) : (
              <>
                {children}
                {buttons && <StyledDialogActions maxWidth={customMaxWidth}>{actions}</StyledDialogActions>}
              </>
            )}
          </ModalContentWrapper>
        </StyledDialogContent>
      </StyledDialog>
    );
  }

  // --- Everyday, regular, dialog ---
  return (
    <StyledDialog aria-labelledby="customized-dialog-title" {...props} open={open}>
      <StyledDialogTitle id="customized-dialog-title" deleteconfirmationmodal={deleteConfirmationModal}>
        {icon && (
          <StyledIconButton
            disableRipple
            aria-label="close"
            onClick={onClose}
            sizeVariant="s"
            icon={faX}
            buttonType="transparentNeutral"
          />
        )}
        <StyledTypography>{title}</StyledTypography>
      </StyledDialogTitle>

      <StyledDialogContent>
        {deleteConfirmationModal ? (
          <ModalContentWrapper maxWidth="100%" deleteConfirmationModal={deleteConfirmationModal}>
            <TypographyTsoPq typographyType="bodyTextMD">{confirmationText}</TypographyTsoPq>
            <StyledDialogActions deleteConfirmationModal={deleteConfirmationModal}>
              <ButtonTsoPq buttonType="outlinedPrimary" onClick={onCancel} textContent={t('general.btn.cancel')} />
              <ButtonTsoPq buttonType="filledPrimary" onClick={onConfirm} textContent={t('general.btn.delete')} />
            </StyledDialogActions>
          </ModalContentWrapper>
        ) : (
          children
        )}
      </StyledDialogContent>

      {buttons ? <DialogActions>{actions || ''}</DialogActions> : ''}
    </StyledDialog>
  );
};

export default ModalDialogTsoPq;
